// components/UserCard/UserCard.tsx
import React, { useEffect, useRef, useState } from "react";
import styles from "./index.module.scss";
import { UserCard as UserCardType } from "../../../types/seatApproval";
import { calculateAges } from "../../../../../utils/commonFunctions";
import defaultProfileIcon from "../../../../../assets/images/default-profile.svg";
import { Avatar, Tooltip } from "@mui/material";
import { Session } from "../../../types/session"; // Adjust the import path as necessary
import SessionsCard from "../../programSessions";
import MoveProgramCard from "../../../../../common/components/MoveProgramCard";
import { useResponsive } from "../../../../../utils/functions";
import { UserData } from "../../../../../types/seatApproval";
import starIcon from "../../../../../assets/images/star.svg";
import { Button } from "../../../../../common/components/Button";
import UniDirection from "../../../../../assets/images/uni-direction.svg";
import { Users } from "lucide-react";
import { ApprovalStatus } from "../../../../../constants/textConstants";


interface PreferenceItem {
  name: string;
}

interface UserCardProps {
  seeker: UserCardType;
  getInitials?: (name: string) => string;
  preferenceData?: PreferenceItem[];
  disablePreferences?: boolean;
  messageSection?: boolean; // Optional class for avatar styling
  sessions?: Session[]; // Replace with actual type if available
  onClose?: () => void; // Function to close the card
  onBless?: (sessionId: number) => void; // Function to handle blessing a
  user?: any;
  handleBlessed?: (seekerId: number,
    targetProgramId: number,
    swapType: string,
    swapRequestId?: number) => void; // Add these
  currentProgram?: string;
  handleSeekerClick?: (user: UserData) => void;
  availablePrograms?: Session[];
  onProgramSelect?: (program: any) => void;
  onCancel?: () => void;
  programId?: string;
  handleSwapUser?: (user: any, programId: any) => void;
  selectedSwapProgram?: any;
  selectedSwapUser?: any;
  selectedOption?: any; // Replace with actual type if available
}

const UserCard: React.FC<UserCardProps> = ({
  seeker,
  preferenceData = [],
  handleSeekerClick,
  disablePreferences = false,
  messageSection = false,
  sessions,
  onBless,
  onClose,
  handleBlessed,
  currentProgram,
  availablePrograms,
  onProgramSelect,
  onCancel,
  programId,
  handleSwapUser,
  selectedSwapProgram,
  selectedSwapUser,
  user,
  selectedOption
}) => {
  // Helper functions
  console.log("UserCard Data:", user);
  const getUserName = (): string => {
    return seeker?.fullName || seeker?.seekerName || "Unknown User";
  };

  const getHdbCount = (): number => {
    console.log("Seeker HDB Data:", seeker);
    return seeker?.noOfHDBs || seeker?.numberOfHDBs || seeker?.completedHDBs || 0;
  };

  const getFormattedHdbCount = (): string => {
    return getHdbCount().toString().padStart(2, "0");
  };

  const getUserGender = (): string => {
    return seeker.gender?.charAt(0)?.toUpperCase() || "";
  };
  const getUserAge = (): string => {
    try {
      if (seeker.age !== undefined && seeker.age !== null) {
        return `${seeker.age} yrs`;
      }
      return seeker.dob ? `${calculateAges(seeker.dob)} yrs` : "N/A";
    } catch (error) {
      console.error("Error calculating age:", error);
      return "N/A";
    }
  };

  const getUserLocation = (): string | null => {
    return seeker.city || seeker.location || null;
  };

  const getMessageSection = (): boolean => {
    return messageSection;
  };

  const { isMobileResolution, isTabletResolution } = useResponsive();
  const getFormattedAppliedDate = (): string => {
    const dateStr = seeker.createdAt || seeker.appliedOn;
    if (!dateStr) return "N/A";

    try {
      // Add 5 hours 30 minutes (19800000 ms) to convert UTC to IST
      const utcDate = new Date(dateStr);
      const istDate = new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000);
      return istDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: "UTC", // Keep as UTC so the added offset is reflected
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const shouldShowMahatriaChoice = (): boolean => {
    return !disablePreferences && preferenceData.length === 0;
  };

  const shouldShowPreferences = (): boolean => {
    return !disablePreferences && preferenceData.length > 0;
  };

  // Render components
  const renderUserAvatar = () => (
    <div
      className={isTabletResolution ? styles.userAvatarTab : styles.userAvatar}
    >
      <Avatar
        alt="Remy Sharp"
        src={seeker.profileImage || seeker.profileUrl || defaultProfileIcon}
        sx={{
          width: "100%",
          height: "100%",
          aspectRatio: "1 / 1",
          borderRadius: "50%",
          border: "1px solid #fff",
          objectFit: "cover",
          display: "block",
        }}
        data-testid="user-avatar"
      />
      <div className={styles.hdbBadgeWrapper}>
        {seeker?.preferredRoomMate && <div className={styles.hdbDot}></div>}
        <div className={styles.hdbBadge}>{getFormattedHdbCount()} HDBs</div>
      </div>
    </div>
  );

  const renderUserDetails = () => {
    const userLocation = getUserLocation();

    return (
      <div className={styles.userDetails}>
        {(isMobileResolution || isTabletResolution) && (
          <>
            <span className={styles.usersubdetails}>{getUserLocation()}</span>
            {user.rating && (
              <>
                <span className={styles.divider}>|</span>
                <div className={styles.ratingContainer}>
                  <img
                    src={starIcon}
                    alt="Star Icon"
                    className={styles.starIcon}
                  />
                  <span className={styles.averageRating}>
                    {user?.rating || 0}
                  </span>
                </div>
              </>
            )}
          </>
        )}
        {!isMobileResolution && !isTabletResolution && (
          <>
            <span className={styles.usersubdetails}>{getUserGender()}</span>
            <span className={styles.divider}>|</span>
            <span className={styles.usersubdetails}>{getUserAge()}</span>
            {/* <span className={styles.divider}>|</span>
        <span className={styles.usersubdetails}>
          {getFormattedHdbCount()} Programs
        </span> */}
            {userLocation && (
              <>
                <span className={styles.divider}>|</span>
                <span className={styles.usersubdetails}>{userLocation}</span>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  const renderMahatriaChoice = () => (
    <span className={styles.mahatriaChip}>
      {/* <span className={styles.mahatriaRed}>Mahatria's</span> */}
      <span className={styles.mahatriaBlue}>Any HDB/MSD</span>
    </span>
  );

  const preferencesRef = useRef<HTMLDivElement>(null);

  const renderPreferences = () => (
    <>
      {preferenceData.map((pref, idx) => (
        <span
          key={`pref-${idx}-${pref.name}`}
          className={styles.preferenceChip}
        >
          {pref.name}
        </span>
      ))}
    </>
  );

  const renderUserPreferences = () => {
    if (disablePreferences) return null;
    return (
      <div className={styles.userPreferences}>
        {shouldShowMahatriaChoice()
          ? renderMahatriaChoice()
          : (isMobileResolution || isTabletResolution) ? (preferenceData.map((p) => p.name).join(", ")) : (renderPreferences())}
      </div>
    );
  };

  const renderRoommateInfo = () => {
    if (!seeker.preferredRoomMate) return null;
    return (
      <div className={styles.roommateContent}>
        <div className={styles.hdbpinkDot}></div>
        <div className={styles.roommatePreference}>Roommate preference</div>
        <div className={styles.roommateName}>{seeker.preferredRoomMate}</div>
      </div>
    );
  };

  return (
    <div className={styles.userCardWrapper}>
      <div
        className={
          !handleSeekerClick
            ? styles.seekerDetailsContainer
            : styles.userCardOnHome
        }
      >
        <div
          className={
            !handleSeekerClick ? styles.userCard : styles.userDetailsCardHome
          }
          onClick={() => handleSeekerClick && handleSeekerClick(user.id)}
        >
          <div className={styles.userCardContent}>
            {renderUserAvatar()}
            <div className={styles.userInfo}>
              {(!isMobileResolution && !isTabletResolution) && (
                <div className={styles.appliedDate}>
                  <p className={styles.appliedOn}>
                    applied on {getFormattedAppliedDate()}
                  </p>
                </div>
              )}
              <h3 className={styles.userName}>{getUserName()}</h3>
              {renderUserDetails()}
              <p className={styles.appliedDateMobile}>
                {getFormattedAppliedDate()}
              </p>
              <div className={styles.allocatedProgram}>
                {(isMobileResolution || isTabletResolution) ? (
                  user?.allocatedProgram !== null ? (
                    <>
                      <p className={styles[user.approvalStatus]}>
                        {user?.allocatedProgram?.name}
                      </p>
                      {/* {!handleSeekerClick && (
        <div className={styles.allocatedProgramSeperator}></div>
      )} */}
                    </>
                  ) : user?.approvalStatus === ApprovalStatus.ON_HOLD ? (
                    <p className={styles[user.approvalStatus]}>yet to decide</p>
                  ) : user?.approvalStatus !== ApprovalStatus.PENDING ? (
                    <p className={styles[user.approvalStatus]}>Hold</p>
                  ) : null
                ) : null}
                {user?.swapRequests && user?.swapRequests.length > 0 && (isMobileResolution || isTabletResolution) ? (
                  <>
                    <div>
                      <img src={UniDirection} />
                    </div>
                    <div className={styles.requestedPrefWrapper}>
                      {user.swapRequests && user.swapRequests.length > 0 ? (
                        user.swapRequests.map((req: any, idx: number) => (
                          <span className={styles.requestedPref} key={idx}>
                            {req.name}
                            {idx < user.swapRequests.length - 1 && ", "}
                          </span>
                        ))
                      ) : (
                        <span className={styles.requestedPref}>
                          Any program
                        </span>
                      )}
                    </div>
                  </>) : (
                  (renderUserPreferences())
                )}
              </div>
              {!isMobileResolution && !isTabletResolution && renderRoommateInfo()}
            </div>
          </div>
        </div>
        {(isMobileResolution || isTabletResolution) && !handleSeekerClick && (
          user.approvalStatus === ApprovalStatus.PENDING || user.approvalStatus === ApprovalStatus.ON_HOLD || user.approvalStatus === ApprovalStatus.REJECTED ? (
            <SessionsCard user={user} sessions={sessions} onBless={onBless} onClose={onClose} />
          ) : (
            <>
              <MoveProgramCard
                user={user}
                handleBless={handleBlessed}
                currentProgram={currentProgram}
                availablePrograms={availablePrograms}
                onProgramSelect={onProgramSelect}
                onCancel={onCancel}
                programId={programId}
                handleSwapUser={handleSwapUser}
              />
              <>
                {Object.keys(selectedSwapProgram).length &&
                  !Object.keys(selectedSwapUser).length ? (
                  <div className={styles.userDescription}>
                    moving {user?.fullName} to{" "}
                    {selectedSwapProgram?.name}
                  </div>
                ) : Object.keys(selectedSwapUser).length ? (
                  <div className={styles.userDescription}>
                    {user?.fullName} is being swapped
                    into {selectedSwapProgram?.name}, and{" "}
                    {selectedSwapUser?.fullName} into{" "}
                    {selectedOption.label}.
                  </div>

                ) : null}
              </>
              <div className={styles.swapBlessedButton}>
                <Button
                  type="submit"
                  buttonClassName={`${styles.buttonContainer} ${selectedSwapProgram ? styles.active : ""}`}
                  buttonTextClassName={styles.buttonText}
                  datatestid="add-program-save-button"
                  datatestidText="add-program-save"
                  onClick={() => {
                    console.log("Blessed user:", user?.registrationId);
                    if (user?.registrationId) {
                      handleBlessed(
                        user.registrationId,
                        selectedSwapProgram,
                        "swap",
                        user.swapRequestId,
                      );
                    }
                  }}
                  disable={
                    !selectedSwapProgram ||
                    Object.keys(selectedSwapProgram).length === 0
                  }
                >
                  <span className={styles.blessText}>bless</span>
                </Button>
              </div>
            </>

          )
        )}
      </div>
      {isMobileResolution && !handleSeekerClick && renderRoommateInfo()}
    </div>
  );
};

export default UserCard;
